import type { MedChronTemplate } from "@/components/MedicalChronology/Visits/types";
import { useMedicalChronology } from "@/components/MedicalChronology/contexts/MedicalChronologyContext";
import type { ChronologyVersion } from "@/components/MedicalChronology/types";
import { useChronologyCompareVersionsStore } from "@/store/chronologyCompareVersionsStore";
import React from "react";

import { VersionsCompareView } from "./VersionsCompareView";
import { useChronologyVersionsDiff } from "./hooks/useChronologyVersionsDiff";
import type { CompareVersionsSection } from "./types";

interface VersionsCompareContentProps {
  caseId: string;
  baseVersionId: string;
  compareVersionId: string;
  versions: ChronologyVersion[];
  template: MedChronTemplate | null;
  fileMapping: Record<string, string>;
  onClose: () => void;
  section: CompareVersionsSection;
}

const VersionsCompareContent: React.FC<VersionsCompareContentProps> = ({
  caseId,
  baseVersionId,
  compareVersionId,
  versions,
  template,
  fileMapping,
  onClose,
  section,
}) => {
  const { diff, isLoading, error } = useChronologyVersionsDiff({
    caseId,
    baseVersionId,
    compareVersionId,
  });

  const baseVersion = versions.find((v) => v.version_id === baseVersionId) ?? null;
  const compareVersion = versions.find((v) => v.version_id === compareVersionId) ?? null;

  return (
    <VersionsCompareView
      diff={diff}
      isLoading={isLoading}
      error={error}
      template={template}
      fileMapping={fileMapping}
      baseVersion={baseVersion}
      compareVersion={compareVersion}
      onClose={onClose}
      section={section}
    />
  );
};

interface VersionsCompareProps {
  section: CompareVersionsSection;
}

export const VersionsCompare: React.FC<VersionsCompareProps> = ({ section }) => {
  const { caseId, versions, template, fileMapping } = useMedicalChronology();
  const selectedBaseVersionId = useChronologyCompareVersionsStore((s) => s.selectedBaseVersionId);
  const selectedCompareVersionId = useChronologyCompareVersionsStore(
    (s) => s.selectedCompareVersionId
  );
  const exitCompareMode = useChronologyCompareVersionsStore((s) => s.exitCompareMode);

  if (!selectedBaseVersionId || !selectedCompareVersionId) {
    return null;
  }

  const pickedBase = versions.find((v) => v.version_id === selectedBaseVersionId);
  const pickedCompare = versions.find((v) => v.version_id === selectedCompareVersionId);
  const baseIsOlder =
    !pickedBase ||
    !pickedCompare ||
    new Date(pickedBase.created_at) <= new Date(pickedCompare.created_at);
  const [olderVersionId, newerVersionId] = baseIsOlder
    ? [selectedBaseVersionId, selectedCompareVersionId]
    : [selectedCompareVersionId, selectedBaseVersionId];

  return (
    <VersionsCompareContent
      caseId={caseId}
      baseVersionId={olderVersionId}
      compareVersionId={newerVersionId}
      versions={versions}
      template={template}
      fileMapping={fileMapping}
      onClose={exitCompareMode}
      section={section}
    />
  );
};
